import { Components, ThisGorillaUtils } from "@thisgorilla/primate-ds";
import { Dispatch, SetStateAction, useRef } from "react";
import styled, { useTheme } from "styled-components";
import { API_CALLS } from "../../../../../../API/api_calls";
import { hideScrollBar } from "../../../../../../commonStyles";
import { useClickOutside } from "../../../../../../hooks/useClickOutside";
import {
  actions,
  useAppDispatch,
  useAppSelector,
} from "../../../../../../redux";
import { FadeTransitionType } from "../../../../../../redux/common";
import {
  fadeAnimation,
  FlexColumn,
  FlexRowCenter,
} from "../../../../../Common/Styles";
import { usePostCardContext } from "../PostCardContent";

type props = {
  setCircleDropdownOpen: Dispatch<SetStateAction<boolean>>;
  circleDropdownOpen: boolean;
};

export const UserAddOrRemoveToCircle = ({
  setCircleDropdownOpen,
  circleDropdownOpen,
}: props) => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const { typography, Color } = useTheme();

  const { listOfCircles } = useAppSelector((state) => state.circle);
  const { processed_posts } = useAppSelector((state) => state.newsfeed);
  const { postId } = usePostCardContext();

  useClickOutside({
    ref: containerRef,
    setState: setCircleDropdownOpen,
  });

  if (!postId) return null;
  const original_post = processed_posts[postId];
  const { name, user_id } = original_post.UserDetails;

  return (
    <Container
      $fadeTransitionType={circleDropdownOpen ? "FadeIn" : "FadeOut"}
      ref={containerRef}
    >
      <Components.Menu size="fill" withBackground={true} itemGap={8}>
        <Components.ListItem
          type="Label"
          label={`Add or remove ${name} from your circles.`}
        />
        <Components.ListItem type="Divider" />
        <Wrapper $gapInPx={8}>
          <Components.Text
            style={{ tokenStyle: typography.Body.S }}
            text="Circles"
            color={Color.text["text-emphasize-brand-bolder"].value}
            fontWeight="font-weight-semibold"
            lineHeight="line-height-75"
          />
          {listOfCircles.length > 0 ? (
            <ScrollContainer>
              {listOfCircles.map((circle) => (
                <Components.ListItem
                  key={circle.id}
                  type="Circle"
                  color={circle.colour}
                  label={circle.name}
                  selected={(circle.circle_user_ids || []).includes(user_id)}
                  onClick={() => {
                    API_CALLS.CIRCLE[
                      (circle.circle_user_ids || []).includes(user_id)
                        ? "removeUserFromCircle"
                        : "addUserToCircle"
                    ]({
                      circle_id: circle.id,
                      circle_user_id: user_id,
                    }).then(() => {
                      (circle.circle_user_ids || []).includes(user_id) &&
                        dispatch(actions.newsfeed.setPage(0));
                    });
                  }}
                />
              ))}
            </ScrollContainer>
          ) : (
            <EmptyCircleContainer $gapInPx={8}>
              <Components.Icon
                type="Info"
                color={Color.icon["icon-brand-bolder"].value}
                size="16px"
              />
              <Components.Text
                style={{ tokenStyle: typography.Body.M }}
                text="Looks like you don’t have any Circles yet. Let creat one!"
                color={Color.text["text-emphasize-brand-bolder"].value}
              />
            </EmptyCircleContainer>
          )}
        </Wrapper>
        <Components.ListItem
          type="Button"
          IconType="Plus"
          label="Create a Circle"
          onClick={() => {
            dispatch(
              actions.circle.setCreateCircleOpen({ createCircleOpen: true })
            );
            setCircleDropdownOpen(false);
          }}
        />
      </Components.Menu>
    </Container>
  );
};

const Container = styled(FlexColumn)<{
  $fadeTransitionType: FadeTransitionType;
}>`
  position: absolute;
  top: ${({ theme }) => theme.spacing["spacing-9"].value}rem;
  right: ${({ theme }) => theme.spacing["spacing-none"].value}rem;
  z-index: 2;
  width: 290px;

  ${fadeAnimation}
`;

const ScrollContainer = styled(FlexColumn)`
  max-height: ${ThisGorillaUtils.convertPxToRem("220")}rem;
  overflow-y: auto;
  ${hideScrollBar}
`;

const Wrapper = styled(FlexColumn)`
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  background: linear-gradient(
    116deg,
    #f4fbff 21.57%,
    rgba(230, 236, 255, 0) 76.26%
  );
`;

const EmptyCircleContainer = styled(FlexRowCenter)`
  padding: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-xs"].value}rem;
  background: ${({ theme }) =>
    theme.Color.background.brand.subtlest["default-hover"].value};
`;
